import { Button, TapTheme } from '@tapresearch/ui/components';

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button as ChakraButton,
} from '@chakra-ui/react';

import React from 'react';
import { popupContext } from '../PopupProvider';
import { ConfirmationPopupProps } from './useConfirmationPopup';

export function ConfirmationPopup({
  title = 'Are you sure?',
  description = '',
  confirmationButtonText = 'Accept',
  confirmationButtonLoadingText = 'Submitting',
  cancelButtonText = 'Cancel',
  onConfirm = Function,
  onCancel = Function,
  requestState,
  contentBoxProps = {},
  ...alertDialogProps
}: ConfirmationPopupProps) {
  const cancelRef = React.useRef();

  const [isOpen, setIsOpen] = React.useState(true);
  const onClose = React.useCallback(() => setIsOpen(false), [setIsOpen]);

  const { resetPopup } = React.useContext(popupContext);

  React.useLayoutEffect(() => {
    if (isOpen === false) {
      setTimeout(() => resetPopup(), 600);
    }
  }, [isOpen, resetPopup]);

  const onCancelClick = React.useCallback(() => {
    onCancel();
    onClose();
  }, [onClose, onCancel]);

  const onConfirmationclick = React.useCallback(() => {
    onConfirm();
    if (requestState === undefined) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestState?.status, onClose]);

  React.useEffect(() => {
    if (requestState?.isSuccess) {
      onConfirm();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestState?.status, onClose]);

  const isLoading = React.useMemo(() => {
    return !!requestState && requestState.isLoading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestState?.status]);

  return (
    <AlertDialog
      {...alertDialogProps}
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancelClick}
    >
      <AlertDialogOverlay>
        <AlertDialogContent {...contentBoxProps}>
          <AlertDialogHeader
            data-testid="confirmation-popup-title"
            fontSize="lg"
            fontWeight="bold"
          >
            {title}
          </AlertDialogHeader>

          <AlertDialogBody>{description}</AlertDialogBody>

          <AlertDialogFooter>
            <ChakraButton
              data-testid="confirmation-popup-cancel"
              ref={cancelRef}
              onClick={onCancelClick}
            >
              {cancelButtonText}
            </ChakraButton>

            <Button
              minW="160px"
              isLoading={isLoading}
              loadingText={confirmationButtonLoadingText}
              onClick={onConfirmationclick}
              ml={3}
              backgroundColor={TapTheme.colors.blue[500]}
              color={TapTheme.colors.white}
              _hover={{
                backgroundColor: TapTheme.colors.blue[300],
              }}
            >
              {confirmationButtonText}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
